import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Chip,
  CircularProgress,
} from "@material-ui/core";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import { showToast } from "../../../../utils/toast";
import {
  getPayoutBalanceAPI,
  getMyPayoutsAPI,
} from "../../../../api/payoutsApi";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      backgroundColor: "#1a1a1a",
      borderRadius: "12px",
      padding: "20px",
      border: "1px solid rgba(255, 255, 255, 0.1)",
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "20px",
    },
    title: {
      color: "#FFFFFF",
      fontSize: "18px",
      fontWeight: 600,
    },
    viewAllButton: {
      textTransform: "none",
      fontWeight: 600,
      fontSize: "14px",
      color: "#FFFFFF",
      borderColor: "#FE5A97",
      padding: "6px 14px",
      boxShadow: "none",
      "&:hover": {
        borderColor: "#FE5A97",
        backgroundColor: "rgba(254, 90, 151, 0.08)",
      },
    },
    balanceGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
      gap: "12px",
      marginBottom: "20px",
    },
    balanceCard: {
      backgroundColor: "#0E0E0E",
      border: "1px solid rgba(255, 255, 255, 0.08)",
      borderRadius: "8px",
      padding: "16px",
      textAlign: "center",
    },
    balanceLabel: {
      color: "#FFFFFF",
      fontSize: "12px",
      opacity: 0.8,
      marginBottom: "4px",
    },
    balanceValue: {
      color: "#FFFFFF",
      fontSize: "16px",
      fontWeight: 600,
    },
    pendingSection: {
      marginTop: "20px",
    },
    pendingTitle: {
      color: "#FFFFFF",
      fontSize: "14px",
      fontWeight: 600,
      marginBottom: "12px",
    },
    pendingList: {
      backgroundColor: "#0E0E0E",
      borderRadius: "8px",
      border: "1px solid rgba(255, 255, 255, 0.08)",
    },
    pendingItem: {
      padding: "12px 16px",
      "&:not(:last-child)": {
        borderBottom: "1px solid rgba(255, 255, 255, 0.05)",
      },
    },
    pendingAmount: {
      color: "#FFFFFF",
      fontSize: "14px",
      fontWeight: 600,
    },
    pendingDate: {
      color: "#FFFFFF",
      fontSize: "12px",
      opacity: 0.7,
    },
    statusChip: {
      textTransform: "capitalize",
      fontSize: "10px",
      height: "20px",
    },
    loadingContainer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "40px",
      color: "#FFFFFF",
    },
    emptyState: {
      textAlign: "center",
      padding: "20px",
      color: "#FFFFFF",
      opacity: 0.7,
    },
    requestButton: {
      textTransform: "none",
      fontWeight: 600,
      fontSize: "14px",
      backgroundColor: "#FE5A97",
      color: "#FFFFFF",
      border: "none",
      padding: "8px 18px",
      marginTop: "12px",
      boxShadow: "none",
      "&:hover": {
        backgroundColor: "#e54d88",
        boxShadow: "none",
      },
    },
  })
);

// Types
type PayoutStatus =
  | "pending"
  | "approved"
  | "processing"
  | "paid"
  | "rejected"
  | "canceled";

interface IBalance {
  earnedAvailable: number;
  reserved: number;
  paid: number;
  available: number;
  settlementDays: number;
}

interface IPayoutItem {
  _id: string;
  creatorId: string;
  amount: number;
  currency?: string;
  status: PayoutStatus;
  method?: string;
  note?: string;
  providerRef?: string;
  paidAt?: string;
  createdAt?: string;
}

interface IPayoutListRes {
  items: IPayoutItem[];
  total: number;
  page: number;
  limit: number;
}

const statusColor = (
  status: PayoutStatus
): "default" | "primary" | "secondary" => {
  switch (status) {
    case "approved":
    case "paid":
      return "primary";
    case "processing":
    case "rejected":
      return "secondary";
    default:
      return "default";
  }
};

const formatCurrency = (amount: number): string => `$${amount.toFixed(2)}`;
const formatDate = (dateString?: string): string => {
  if (!dateString) return "—";
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
};

type PayoutStatusOverviewProps = {
  onRequestPayout?: () => void;
  onViewAll?: () => void;
};

const PayoutStatusOverview: React.FC<PayoutStatusOverviewProps> = ({
  onRequestPayout,
  onViewAll,
}) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState<IBalance>({
    earnedAvailable: 0,
    reserved: 0,
    paid: 0,
    available: 0,
    settlementDays: 7,
  });
  const [pendingPayouts, setPendingPayouts] = useState<IPayoutItem[]>([]);
  const [payoutsLoading, setPayoutsLoading] = useState(true);

  const loadBalance = async () => {
    try {
      const data = (await getPayoutBalanceAPI()) as IBalance;
      setBalance(data || ({} as IBalance));
    } catch (error) {
      console.error("Failed to load balance:", error);
      showToast({ message: "Failed to load payout balance", err: true });
    }
  };

  const loadPendingPayouts = async () => {
    setPayoutsLoading(true);
    try {
      const res = (await getMyPayoutsAPI({
        page: 1,
        limit: 5, // Show only first 5 pending payouts
      })) as IPayoutListRes;

      // Filter for pending/processing payouts
      const pending = (res.items || []).filter(
        item => item.status === "pending" || item.status === "processing"
      );
      setPendingPayouts(pending);
    } catch (error) {
      console.error("Failed to load pending payouts:", error);
      showToast({ message: "Failed to load pending payouts", err: true });
    } finally {
      setPayoutsLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([loadBalance(), loadPendingPayouts()]);
      setLoading(false);
    };
    loadData();
  }, []);

  if (loading) {
    return (
      <Box className={classes.container}>
        <div className={classes.loadingContainer}>
          <CircularProgress size={24} style={{ color: "#FE5A97" }} />
          <Typography style={{ marginLeft: "12px" }}>
            Loading payout data...
          </Typography>
        </div>
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      {/* Header */}
      <div className={classes.header}>
        <Typography className={classes.title}>Payout Status</Typography>
        {onViewAll && (
          <Button
            variant="outlined"
            size="small"
            className={classes.viewAllButton}
            onClick={onViewAll}
          >
            View All
          </Button>
        )}
      </div>

      {/* Balance Overview */}
      <div className={classes.balanceGrid}>
        <div className={classes.balanceCard}>
          <Typography className={classes.balanceLabel}>Available</Typography>
          <Typography className={classes.balanceValue}>
            {formatCurrency(balance.available)}
          </Typography>
        </div>
        <div className={classes.balanceCard}>
          <Typography className={classes.balanceLabel}>Reserved</Typography>
          <Typography className={classes.balanceValue}>
            {formatCurrency(balance.reserved)}
          </Typography>
        </div>
        <div className={classes.balanceCard}>
          <Typography className={classes.balanceLabel}>Total Paid</Typography>
          <Typography className={classes.balanceValue}>
            {formatCurrency(balance.paid)}
          </Typography>
        </div>
        <div className={classes.balanceCard}>
          <Typography className={classes.balanceLabel}>Earned</Typography>
          <Typography className={classes.balanceValue}>
            {formatCurrency(balance.earnedAvailable)}
          </Typography>
        </div>
      </div>

      {/* Request Payout Button */}
      {onRequestPayout && balance.available > 0 && (
        <Button
          variant="contained"
          size="small"
          className={classes.requestButton}
          onClick={onRequestPayout}
        >
          Request Payout
        </Button>
      )}

      {/* Pending Payouts */}
      <div className={classes.pendingSection}>
        <Typography className={classes.pendingTitle}>
          Pending Payouts ({pendingPayouts.length})
        </Typography>

        {payoutsLoading ? (
          <div className={classes.loadingContainer}>
            <CircularProgress size={20} style={{ color: "#FE5A97" }} />
          </div>
        ) : pendingPayouts.length > 0 ? (
          <div className={classes.pendingList}>
            {pendingPayouts.map(payout => (
              <div key={payout._id} className={classes.pendingItem}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div>
                    <Typography className={classes.pendingAmount}>
                      {formatCurrency(payout.amount)}
                    </Typography>
                    <Typography className={classes.pendingDate}>
                      {formatDate(payout.createdAt)}
                    </Typography>
                  </div>
                  <Chip
                    size="small"
                    label={payout.status}
                    color={statusColor(payout.status)}
                    className={classes.statusChip}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={classes.emptyState}>
            <Typography>No pending payouts</Typography>
          </div>
        )}
      </div>
    </Box>
  );
};

export default PayoutStatusOverview;
